'use client';

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, HTMLAttributes, ReactNode } from 'react';
import React, { isValidElement } from 'react';
import { ChevronDownIcon, Share04Icon } from '@milanote/roma-icons';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';
import { useRouterLink, type RouterOptions } from '../router';

const SEMANTIC_NAME = 'RomaNavItemBase';

const SIZE_STYLES = {
    sm: {
        root: 'h-9',
        content: 'h-8',
    },
    md: {
        root: 'h-10',
        content: 'h-full',
    },
} as const;

const LEADING_CLASS_NAME =
    'size-5 shrink-0 text-fg-quaternary transition-inherit-all group-hover/item:text-fg-quaternary_hover group-data-[current]/item:text-fg-quaternary_hover group-data-[selected]/item:text-fg-quaternary_hover';

type NavItemType = 'item' | 'collapsible' | 'collapsible-child';

export type NavListSize = keyof typeof SIZE_STYLES;

type CommonNavItemProps = {
    /** Whether the nav item is currently active. */
    current?: boolean;
    /** Whether to animate hover state changes. */
    transition?: boolean;
    /** Content to show before the label. */
    leading?: FC<{ className?: string }> | ReactNode;
    /** Content to show after the label. */
    trailing?: ReactNode;
    /** Whether to truncate the label text. */
    truncate?: boolean;
    /** Size of the nav item. */
    size?: NavListSize;
    /** Additional CSS classes for the outer shell. */
    className?: string;
    /** Content to display. */
    children?: ReactNode;
};

export type NavItemBaseProps = CommonNavItemProps &
    Omit<HTMLAttributes<HTMLElement>, 'className' | 'children' | 'type'> & {
        /** URL to navigate to when the nav item is clicked. Renders a link when provided. */
        href?: string;
        /** Link target for linked nav items. */
        target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
        /** Link relationship for linked nav items. */
        rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
        /** Download behaviour for linked nav items. */
        download?: AnchorHTMLAttributes<HTMLAnchorElement>['download'];
        /** Options applied by the configured client-side router. */
        routerOptions?: RouterOptions;
        /** Native button type for action nav items. */
        type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
        /** Whether the action nav item is disabled. */
        disabled?: ButtonHTMLAttributes<HTMLButtonElement>['disabled'];
        /** Whether the collapsible nav item is open. */
        open?: boolean;
        /** Type of the nav item. */
        navItemType?: NavItemType;
    };

type NavItemContentProps = Pick<CommonNavItemProps, 'children' | 'leading' | 'trailing' | 'truncate'> & {
    isExternal?: boolean;
    navItemType?: NavItemType;
};

type NavItemClassNameOptions = {
    size: NavListSize;
    current?: boolean;
    transition?: boolean;
    navItemType?: NavItemType;
};

/** Renders a nav item leading slot from either a React element or a component reference. */
const renderLeading = (Leading: CommonNavItemProps['leading']) => {
    if (isValidElement(Leading)) return Leading;
    if (isReactComponent(Leading)) return <Leading aria-hidden="true" className={LEADING_CLASS_NAME} />;
    return null;
};

/** Renders the icon, label, trailing content, and affordances for a nav row. */
export const NavItemContent: React.FC<NavItemContentProps> = ({
    children,
    isExternal,
    leading,
    trailing,
    truncate,
    navItemType = 'item',
}) => (
    <>
        <span className="flex min-w-0 flex-1 items-center gap-md">
            {renderLeading(leading)}
            <span
                className={cx(
                    'text-md font-medium tracking-tight text-secondary transition-inherit-all group-hover/item:text-secondary_hover group-data-[current]/item:text-primary group-data-[selected]/item:text-primary',
                    truncate && 'truncate',
                )}
            >
                {children}
            </span>
        </span>
        {isExternal && <Share04Icon aria-hidden="true" className="ml-auto size-4 shrink-0 text-fg-quaternary" />}
        {trailing}
        {navItemType === 'collapsible' && (
            <ChevronDownIcon
                aria-hidden="true"
                className="ml-auto size-4 shrink-0 text-fg-quaternary group-data-[open]/item:-scale-y-100"
            />
        )}
    </>
);

/**
 * Shares nav row styling with roots NavItemBase doesn't render, such as a React Aria `Tab`.
 * The root must set `data-current`, or `data-selected` as React Aria does, so the label and icon colours follow.
 */
export const getNavItemClassNames = ({
    size,
    current,
    transition = true,
    navItemType = 'item',
}: NavItemClassNameOptions) => {
    const sizeStyles = SIZE_STYLES[size];

    const root = cx(
        'group/item flex w-full cursor-pointer items-center py-0.25 outline-hidden select-none',
        sizeStyles.root,
    );

    const content = cx(
        'flex min-w-0 flex-1 items-center',
        'gap-3 rounded-sm px-3 py-2.5',
        'bg-primary',
        transition && 'transition duration-100 ease-linear',
        'group-focus-visible/item:ring-2 group-focus-visible/item:ring-focus-ring group-focus-visible/item:ring-inset',
        sizeStyles.content,
        navItemType === 'collapsible-child' && 'pr-3 pl-12',
        current && 'bg-active',
        'group-hover/item:bg-primary_hover',
    );

    return { root, content };
};

/** Renders the shared base for Roma navigation rows using Figma nav item spacing. */
export const NavItemBase = React.forwardRef<HTMLButtonElement | HTMLAnchorElement | HTMLElement, NavItemBaseProps>(
    (props, ref) => {
        const {
            current,
            navItemType = 'item',
            leading,
            trailing,
            children,
            truncate = true,
            size = 'sm',
            className,
            open,
            transition = true,
            routerOptions,
            ...otherProps
        } = props;
        const href = 'href' in props ? props.href : undefined;
        const { href: resolvedHref, render } = useRouterLink(href, routerOptions);

        const isExternal = href?.startsWith('http');
        const navItemClassNames = getNavItemClassNames({ size, current, transition, navItemType });
        const rootClassName = cx(SEMANTIC_NAME, navItemClassNames.root, className);
        const contentClassName = navItemClassNames.content;

        const content = (
            <NavItemContent
                leading={leading}
                trailing={trailing}
                isExternal={isExternal}
                truncate={truncate}
                navItemType={navItemType}
            >
                {children}
            </NavItemContent>
        );

        const commonProps = {
            className: rootClassName,
            'data-current': current || undefined,
        };

        if (navItemType === 'collapsible') {
            return (
                <summary
                    {...(otherProps as HTMLAttributes<HTMLElement>)}
                    {...commonProps}
                    ref={ref as React.Ref<HTMLElement>}
                    data-open={open || undefined}
                >
                    <span className={contentClassName}>{content}</span>
                </summary>
            );
        }

        if (href) {
            const ariaLinkProps = otherProps as Omit<
                React.ComponentPropsWithoutRef<typeof AriaLink>,
                'children' | 'className'
            >;

            return (
                <AriaLink
                    {...commonProps}
                    {...ariaLinkProps}
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    href={resolvedHref}
                    target={isExternal ? '_blank' : ariaLinkProps.target}
                    render={render}
                    rel={isExternal ? 'noopener noreferrer' : ariaLinkProps.rel}
                    aria-current={current ? 'page' : undefined}
                >
                    <span className={contentClassName}>{content}</span>
                </AriaLink>
            );
        }

        const { type = 'button', ...ariaButtonProps } = otherProps as Omit<
            React.ComponentPropsWithoutRef<typeof AriaButton>,
            'children' | 'className'
        > & {
            type?: AriaButtonProps['type'];
        };

        return (
            <AriaButton {...commonProps} {...ariaButtonProps} ref={ref as React.Ref<HTMLButtonElement>} type={type}>
                <span className={contentClassName}>{content}</span>
            </AriaButton>
        );
    },
);

NavItemBase.displayName = SEMANTIC_NAME;
